import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Alert, CircularProgress, Switch, Grid,
  Tabs, Tab, Divider, Stack, Avatar, alpha, IconButton, Chip, FormControl,
  Select, MenuItem,
} from '@mui/material';
import {
  SmartToy, Settings, Psychology, Refresh, CheckCircle, Speed,
  ModelTraining, MonitorHeart, Memory, VpnKey, Visibility, VisibilityOff,
} from '@mui/icons-material';
import { settingsAPI } from '../../api/client';
import { integerOnly } from '../../utils/inputHelpers';

function AdminAISettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [ai, setAi] = useState({
    enabled: 'true', model: 'gpt-4', temperature: '0.7',
    max_tokens: '2000', context_window: '10',
    property_recommendations: 'true', chat_assistant: 'true',
    auto_categorize_tickets: 'true', sentiment_analysis: 'true',
    language: 'en', rate_limit_per_min: '30',
    content_moderation: 'true', smart_pricing: 'false',
    ai_provider: 'openai', ai_openai_api_key: '', ai_anthropic_api_key: '', ai_openai_org_id: '',
  });
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);

  useEffect(() => {
    setLoading(true);
    settingsAPI.list({ category: 'ai' }).then(res => {
      if (Array.isArray(res.data)) {
        setSettings(res.data);
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setAi(p => ({ ...p, ...m }));
      }
    }).catch(() => setError('Failed to load AI settings'))
    .finally(() => setLoading(false));
  }, []);

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setAi(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #6366f1, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            AI Assistant Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">Configure AI capabilities, training, and monitoring</Typography>
        </Box>
        <IconButton onClick={() => window.location.reload()} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="AI Capabilities" icon={<Psychology sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Model Settings" icon={<ModelTraining sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Monitoring" icon={<MonitorHeart sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Core AI Features</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {[
                  { key: 'enabled', label: 'Enable AI Assistant', desc: 'Master toggle for all AI features' },
                  { key: 'chat_assistant', label: 'Chat Assistant', desc: 'AI-powered help in chat' },
                  { key: 'property_recommendations', label: 'Property Recommendations', desc: 'Smart property suggestions for users' },
                  { key: 'auto_categorize_tickets', label: 'Auto-Categorize Tickets', desc: 'AI classifies support tickets' },
                  { key: 'sentiment_analysis', label: 'Sentiment Analysis', desc: 'Analyze user feedback sentiment' },
                  { key: 'content_moderation', label: 'Content Moderation', desc: 'AI-powered content filtering' },
                  { key: 'smart_pricing', label: 'Smart Pricing', desc: 'AI-suggested pricing for listings' },
                ].map(item => (
                  <Stack key={item.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Box><Typography fontWeight={600}>{item.label}</Typography><Typography variant="body2" color="text.secondary">{item.desc}</Typography></Box>
                    <Switch checked={ai[item.key]==='true'} onChange={(e)=>saveSetting(item.key, e.target.checked?'true':'false')} />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>AI Capabilities Overview</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {[
                  { name: 'Property Search Assistant', desc: 'Helps users find ideal accommodations', icon: <SmartToy />, color: '#6366f1' },
                  { name: 'Ticket Auto-Classification', desc: 'Routes tickets to correct category', icon: <Psychology />, color: '#3b82f6' },
                  { name: 'Fraud Detection', desc: 'Identifies suspicious activities', icon: <CheckCircle />, color: '#ef4444' },
                  { name: 'Price Optimization', desc: 'Suggests optimal listing prices', icon: <Speed />, color: '#10b981' },
                  { name: 'Review Analysis', desc: 'Summarizes and analyzes reviews', icon: <Memory />, color: '#f59e0b' },
                ].map((cap, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: alpha(cap.color, 0.1), color: cap.color, width: 36, height: 36 }}>{cap.icon}</Avatar>
                      <Box><Typography fontWeight={600}>{cap.name}</Typography><Typography variant="caption" color="text.secondary">{cap.desc}</Typography></Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Model Configuration</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>AI Model</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={ai.model} onChange={(e) => saveSetting('model', e.target.value)}>
                      <MenuItem value="gpt-4">GPT-4</MenuItem>
                      <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                      <MenuItem value="claude-3">Claude 3</MenuItem>
                      <MenuItem value="gemini-pro">Gemini Pro</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {[
                  { key: 'temperature', label: 'Temperature', desc: 'Creativity level (0-1)' },
                  { key: 'max_tokens', label: 'Max Tokens', desc: 'Maximum response length' },
                  { key: 'context_window', label: 'Context Window', desc: 'Messages to include as context' },
                ].map(item => (
                  <Box key={item.key}><Typography fontWeight={600} gutterBottom>{item.label}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{item.desc}</Typography>
                    <TextField size="small" type="number" value={ai[item.key]}
                      onChange={(e)=>setAi(p=>({...p,[item.key]:e.target.value}))}
                      onKeyDown={integerOnly}
                      onBlur={(e)=>saveSetting(item.key, e.target.value)} sx={{ width: 140 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <VpnKey color="primary" />
                <Typography variant="h6" fontWeight={700}>API Keys & Provider</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box>
                  <Typography fontWeight={600} gutterBottom>AI Provider</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Select which AI provider to use for all AI features</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={ai.ai_provider} onChange={(e) => saveSetting('ai_provider', e.target.value)}>
                      <MenuItem value="openai">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontWeight={600}>OpenAI</Typography>
                          <Chip label="GPT-4, GPT-3.5" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Stack>
                      </MenuItem>
                      <MenuItem value="anthropic">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontWeight={600}>Anthropic</Typography>
                          <Chip label="Claude 3" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Stack>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>OpenAI API Key</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Get your key from platform.openai.com/api-keys</Typography>
                  <TextField size="small" fullWidth placeholder="sk-..."
                    type={showOpenAIKey ? 'text' : 'password'}
                    value={ai.ai_openai_api_key}
                    onChange={(e) => setAi(p => ({ ...p, ai_openai_api_key: e.target.value }))}
                    onBlur={(e) => { if (e.target.value !== '') saveSetting('ai_openai_api_key', e.target.value); }}
                    InputProps={{
                      endAdornment: (
                        <IconButton size="small" onClick={() => setShowOpenAIKey(!showOpenAIKey)}>
                          {showOpenAIKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      ),
                    }}
                  />
                  {ai.ai_openai_api_key && <Chip label="Key configured" size="small" color="success" sx={{ mt: 1 }} icon={<CheckCircle />} />}
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>OpenAI Organization ID</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Optional — only needed for org-level access</Typography>
                  <TextField size="small" fullWidth placeholder="org-..."
                    value={ai.ai_openai_org_id}
                    onChange={(e) => setAi(p => ({ ...p, ai_openai_org_id: e.target.value }))}
                    onBlur={(e) => { if (e.target.value !== '') saveSetting('ai_openai_org_id', e.target.value); }}
                  />
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Anthropic API Key</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Get your key from console.anthropic.com/settings/keys</Typography>
                  <TextField size="small" fullWidth placeholder="sk-ant-..."
                    type={showAnthropicKey ? 'text' : 'password'}
                    value={ai.ai_anthropic_api_key}
                    onChange={(e) => setAi(p => ({ ...p, ai_anthropic_api_key: e.target.value }))}
                    onBlur={(e) => { if (e.target.value !== '') saveSetting('ai_anthropic_api_key', e.target.value); }}
                    InputProps={{
                      endAdornment: (
                        <IconButton size="small" onClick={() => setShowAnthropicKey(!showAnthropicKey)}>
                          {showAnthropicKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      ),
                    }}
                  />
                  {ai.ai_anthropic_api_key && <Chip label="Key configured" size="small" color="success" sx={{ mt: 1 }} icon={<CheckCircle />} />}
                </Box>
              </Stack>
            </Paper>
            <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Language & Limits</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>Default Language</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={ai.language} onChange={(e) => saveSetting('language', e.target.value)}>
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="tw">Twi</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Rate Limit (requests/min)</Typography>
                  <TextField size="small" type="number" value={ai.rate_limit_per_min}
                    onChange={(e)=>setAi(p=>({...p,rate_limit_per_min:e.target.value}))}
                    onKeyDown={integerOnly}
                    onBlur={(e)=>saveSetting('rate_limit_per_min', e.target.value)} sx={{ width: 120 }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Usage Statistics</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { label: 'API Calls Today', value: '1,247', color: '#6366f1' },
                  { label: 'Tokens Used', value: '856K', color: '#3b82f6' },
                  { label: 'Avg Response Time', value: '1.2s', color: '#10b981' },
                  { label: 'Error Rate', value: '0.3%', color: '#ef4444' },
                ].map((stat, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={600}>{stat.label}</Typography>
                      <Typography variant="h6" fontWeight={800} color={stat.color}>{stat.value}</Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Dynamic Settings from Backend</Typography><Divider sx={{ mb: 2 }} />
              {settings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No settings found in database</Typography>
              ) : (
                <Stack spacing={2}>
                  {settings.map(s => (
                    <Box key={s.key}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>{s.description || s.key}</Typography>
                      {(s.value === 'true' || s.value === 'false') ? (
                        <Switch checked={s.value==='true'} onChange={(e)=>saveSetting(s.key, e.target.checked?'true':'false')} />
                      ) : (
                        <TextField size="small" fullWidth defaultValue={s.value}
                          onBlur={(e) => { if (e.target.value !== s.value) saveSetting(s.key, e.target.value); }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default AdminAISettingsPage;
